import { onSiteFieldOf } from '../../services/helpers/OrderMethodAliases';

/**
 * Pure mapping between a TaxCategoryResource (API) and the draft shape the
 * form/useForm hooks in TaxCategoryPanel work with. Kept separate from the
 * component so the mapping — especially which rates are sent as null — is
 * unit-testable without mounting antd.
 */

export const BLANK_TAX_CATEGORY_DRAFT = {
  name: '',
  default_tax_rate: null,
  pickup_tax_rate: null,
  delivery_tax_rate: null,
  dine_in_tax_rate: null,
  on_site_tax_rate: null,
  show_zero_tax_in_summary: false,
};

/**
 * Load status of a company's tax-category list, as tracked by whoever fetches
 * it imperatively (see views/menu/menu/index.jsx) and consumed by
 * TaxCategoryPicker to tell "not loaded yet" / "failed to load" apart from
 * "genuinely zero categories" — the distinction the silent-unlink bug fell
 * through.
 */
export const TAX_CATEGORIES_STATUS = Object.freeze({
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
});

/**
 * TaxCategoryResource -> editable draft. `category` is null for a brand new,
 * unsaved panel.
 */
export function toFormValues(category) {
  if (!category) {
    return { ...BLANK_TAX_CATEGORY_DRAFT };
  }

  return {
    name: category.name ?? '',
    default_tax_rate: category.default_tax_rate ?? null,
    pickup_tax_rate: category.pickup_tax_rate ?? null,
    delivery_tax_rate: category.delivery_tax_rate ?? null,
    dine_in_tax_rate: category.dine_in_tax_rate ?? null,
    // A backend from before the rename sends `room_service_tax_rate` (PROD-724).
    on_site_tax_rate: onSiteFieldOf(category, 'on_site_tax_rate', 'room_service_tax_rate') ?? null,
    show_zero_tax_in_summary: Boolean(category.show_zero_tax_in_summary),
  };
}

/**
 * Draft -> CreateTaxCategoryRequest/UpdateTaxCategoryRequest payload.
 * `dine_in_tax_rate` is the prototype's "In restaurant" field.
 * A rate left empty is sent as null, and the category falls back to
 * `default_tax_rate` for that order method.
 */
export function toRequestPayload(draft) {
  return {
    name: (draft.name ?? '').trim(),
    default_tax_rate: toNumberOrNull(draft.default_tax_rate),
    pickup_tax_rate: toNumberOrNull(draft.pickup_tax_rate),
    delivery_tax_rate: toNumberOrNull(draft.delivery_tax_rate),
    dine_in_tax_rate: toNumberOrNull(draft.dine_in_tax_rate),
    on_site_tax_rate: toNumberOrNull(draft.on_site_tax_rate),
    show_zero_tax_in_summary: Boolean(draft.show_zero_tax_in_summary),
  };
}

export function isDraftSaveable(draft) {
  return Boolean((draft.name ?? '').trim()) && toNumberOrNull(draft.default_tax_rate) !== null;
}

/**
 * FormData fragment for tax_category_id. The per-row tax_percentage is retired
 * on the backend, so a rate is only ever expressed through a tax category and
 * nothing here sends one.
 *
 * The server only reads tax_category_id as "explicitly unlinked" when the key
 * is present with a null value - createFormData drops null/undefined keys
 * entirely, so an unlink has to be sent as an empty string (the
 * ConvertEmptyStringsToNull middleware turns that back into null server-side).
 * A product/category that never had a tax category keeps omitting the key.
 *
 * `canUnlink` gates the unlink case: when the tax-category list hasn't loaded
 * (or failed to), the caller cannot tell a genuine unlink from a category that
 * merely failed to resolve in the picker, so it must not send one — {} is a
 * no-op on the link either way, never a silent destroy.
 *
 * `clearsOfferedDefault` is a new row whose pre-selected default the merchant
 * cleared. Omitting the key would have the backend link that very default
 * again, so "nothing" is said explicitly. A new row that was never offered one
 * still omits the key, and the backend links the menu default rate.
 */
export function buildTaxCategoryFields({
  taxCategoryId,
  wasLinkedTaxCategoryId,
  canUnlink = true,
  clearsOfferedDefault = false,
}) {
  if (taxCategoryId) {
    return { tax_category_id: taxCategoryId };
  }

  if ((wasLinkedTaxCategoryId || clearsOfferedDefault) && canUnlink) {
    return { tax_category_id: '' };
  }

  return {};
}

/**
 * The category a new menu row is linked to when nothing is picked: flagged
 * is_default by GET /menu/tax-categories?rate= (ResolveTaxCategoryForRate on
 * the backend, which a restaurant's own fork of a region category wins).
 * Pre-selected so the form shows what will be saved.
 */
export function defaultTaxCategoryId(taxCategories) {
  return (taxCategories ?? []).find((category) => category.is_default)?.id ?? null;
}

/**
 * Whether to offer the inline "add a tax category" control in the menu modals.
 *
 * The gate is WriteCompanySettings, because that is what the /tax-categories
 * route group is gated on server-side — not WriteMenu, and not the screen ACL:
 * canAccessScreen resolves against per-role *blocked* lists, so it answers "may
 * reach the screen", never "holds the right". Gating on either let a
 * StoreOperator, ContentManager or SDR see the control and collect a 403 on
 * submit.
 *
 * On top of that, a master menu creates a category owned by the franchise's
 * service provider and inherited by every outlet, so it takes WriteMasterMenu
 * as well — and needs no selected company, unlike the company-scoped case (see
 * the guard in views/menu/menu/index.jsx's requestTaxCategories).
 */
export function canCreateTaxCategory({
  isMasterMenu = false,
  hasSelectedCompany = false,
  canWriteCompanySettings = false,
  canWriteMasterMenu = false,
}) {
  if (!canWriteCompanySettings) {
    return false;
  }

  return isMasterMenu ? canWriteMasterMenu : hasSelectedCompany;
}

/**
 * Structured summary of a tax category's rate(s) for display in a picker
 * option — kept as data, not a formatted string, so the component owns
 * formatting/i18n.
 */
export function taxCategoryRateSummary(category) {
  const defaultRate = toNumberOrNull(category?.default_tax_rate);
  const hasChannelOverrides = [
    category?.pickup_tax_rate,
    category?.delivery_tax_rate,
    category?.dine_in_tax_rate,
    onSiteFieldOf(category, 'on_site_tax_rate', 'room_service_tax_rate'),
  ].some((rate) => toNumberOrNull(rate) !== null);

  return { defaultRate, hasChannelOverrides };
}

/**
 * Whether the TaxCategoryPicker belongs on a product/category form at all.
 *
 * Every outlet gets it. It used to hang on the outlet's
 * has_dedicated_products_tax, but that flag is copied from the region and only
 * says whether the region has a *reduced* product rate (DE 7%). Regions
 * without one (CA, US…) never saw the picker, which is now the only place a
 * per-item rate can be set - so a Canadian partner had no way to charge
 * liquor differently from food. The backend accepts and charges
 * tax_category_id regardless of the flag.
 *
 * A company-scoped menu still needs a selected outlet: its tax categories are
 * loaded and created per outlet (see views/menu/menu's requestTaxCategories).
 * A master menu edits franchise-wide subjects whose tax category is owned by
 * the franchise's service provider, so it needs no outlet at all.
 */
export function showsTaxCategoryPicker({ isMaster = false, company = null } = {}) {
  return Boolean(isMaster || company?.id);
}

/**
 * Whether the combo-tax switch belongs on a product/category form.
 *
 * Unlike the picker, this one still follows has_dedicated_products_tax: combo
 * tax splits a price between the full and the reduced product rate, and a
 * region without a reduced rate has nothing to split it into. Offering it there
 * only gives the merchant a way to hide the picker and unlink the category
 * they just chose (save() sends no tax_category_id while combo tax is on).
 *
 * `wasComboTax` is the saved row's value, not the form's: a row that already
 * has combo tax on keeps the switch, so the merchant can turn it off and reach
 * the picker, but turning it off doesn't make the switch vanish mid-edit.
 */
export function showsComboTaxSwitch({ isMaster = false, company = null, wasComboTax = false } = {}) {
  if (!showsTaxCategoryPicker({ isMaster, company })) {
    return false;
  }

  return Boolean(isMaster || company?.has_dedicated_products_tax || wasComboTax);
}

/**
 * Status transition for a list that has already loaded once.
 *
 * A READY list is the user's working copy: a picker may be open against it
 * mid-edit. Neither a refetch in flight (LOADING, which swaps the Select for a
 * skeleton) nor a refetch that failed (ERROR, which removes the Select entirely
 * and pins canUnlink to false) may take it away — only a first load is allowed
 * to land on either. Stale beats gone; the caller still toasts the failure.
 */
export function keepLoadedStatus(current, fallback) {
  return current === TAX_CATEGORIES_STATUS.READY ? current : fallback;
}

/**
 * Who owns a category, as TaxCategoryResource's `scope` reports it. Drives the
 * region-wide / partner-wide / restaurant-wide label.
 */
export const CATEGORY_SCOPE = Object.freeze({
  REGION: 'region',
  PARTNER: 'partner',
  RESTAURANT: 'restaurant',
});

/**
 * Whether the acting screen may rewrite this category in place. Anything but
 * an explicit false counts as owned: the menu picker's resource omits
 * is_editable, and a brand-new draft has no category at all.
 */
export function isOwnedCategory(category) {
  return category?.is_editable !== false;
}

/**
 * Whether this screen may edit the category at all. A restaurant screen edits
 * everything it lists - its own rows in place, the region's and partner's by
 * forking them into the restaurant. The partner screen has no restaurant to
 * fork into, so the region's rows are read-only there.
 */
export function isEditableHere({ category, isPartnerScreen = false }) {
  return isOwnedCategory(category) || !isPartnerScreen;
}

/**
 * Whether a save changes what any linked product charges, i.e. whether it
 * needs the confirmation at all for a category edited in place. A rename or
 * the summary toggle does not.
 */
export function changesRates(category, payload) {
  const current = toRequestPayload(toFormValues(category));

  return ['default_tax_rate', 'pickup_tax_rate', 'delivery_tax_rate', 'dine_in_tax_rate', 'on_site_tax_rate'].some(
    (field) => current[field] !== payload[field],
  );
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);

  return Number.isNaN(parsed) ? null : parsed;
}
